// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Collapse } from "react-bootstrap";

import { IAction, ActionType, raiseAction } from "actions";
import { tabClickHandler } from "guiutils";
import { IState } from "states/states";
import { IOcppCharger, IRefusedRfid } from "states/ocppstates";
import { ITableSort } from "states/guistates";

import { ReactComponent as IconAdd } from "static/iconAdd.svg";
import { ReactComponent as IconExpandMore } from "static/iconExpandMore.svg";
import { ReactComponent as IconExpandLess } from "static/iconExpandLess.svg";

import FormattedDate from "./FormattedDate";
import FormattedTime from "./FormattedTime";
import { isSet } from "utils";

// sort key of table
const TABLE_NAME = "t_rfids";

interface IRefusedRfidsProps {
  refusedrfids: IRefusedRfid[] | undefined;
  ocppChargers: IOcppCharger[];
  tableSort: ITableSort | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: IRefusedRfidsProps = {
    refusedrfids: state.ocpp.refusedRfid,
    ocppChargers: state.ocpp.charger,
    tableSort: state.gui.tableSort[TABLE_NAME],
  };
  return props;
};

interface IRefusedRfidsState {
  isExpanded: boolean;
  showAll: boolean;
}

interface IRefusedRfidsDispatch {
  onAddRfid: (rfid: string) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IRefusedRfidsDispatch = {
    onAddRfid(rfid: string) {
      dispatch(raiseAction(ActionType.ACT_EDIT_RFID, rfid));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msgAdd: {
    id: "add this as new charging card",
  },
});

class RefusedRfidsComponent extends React.Component<
  IRefusedRfidsProps & IRefusedRfidsDispatch & WrappedComponentProps,
  IRefusedRfidsState
> {
  state: IRefusedRfidsState = {
    isExpanded: true,
    showAll: false,
  };

  public render() {
    const rfidItems = this.props.refusedrfids?.map((curItem) => {
      return this.renderRfid(curItem);
    });

    return (
      <div id="refusedrfids">
        <h1
          className="link"
          onClick={() => {
            this.setState({ isExpanded: !this.state.isExpanded });
          }}
        >
          <Button variant="link" className="expand-button me-2 d-print-none">
            {this.state.isExpanded ? <IconExpandMore /> : <IconExpandLess />}
          </Button>
          <FormattedMessage id="Refused Charging Cards" />
        </h1>
        <Collapse in={this.state.isExpanded}>
          <div id="refusedrfidtable">
            {isSet(rfidItems) && rfidItems.length > 0 ? (
              <table className="datatable fullwidthwhite">
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage id="Card Number" />
                    </th>
                    <th>
                      <FormattedMessage id="Charger" />
                    </th>
                    <th className="optional">
                      <FormattedMessage id="First Attempt" />
                    </th>
                    <th>
                      <FormattedMessage id="Last Attempt" />
                    </th>
                    <th className="center optional">
                      <FormattedMessage id="Counter" />
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>{rfidItems}</tbody>
              </table>
            ) : (
              <div className="ms-2" style={{ paddingTop: 5, paddingBottom: 5 }}>
                <FormattedMessage id="No refused charging cards detected within the last 7 days." />
              </div>
            )}
          </div>
        </Collapse>
      </div>
    );
  }

  private renderRfid(rfid: IRefusedRfid) {
    const charger = this.props.ocppChargers.find(
      (curCharger) => curCharger.fk_charger === rfid.charger,
    );

    return (
      <tr key={`${rfid.rfid}-${charger}`}>
        <td>{rfid.rfid}</td>
        <td>{charger?.name ?? charger?.chargepoint}</td>
        <td className="optional">
          <FormattedDate valueMs={rfid.firstAttempt * 1000} doZeroPadding={true} />{" "}
          <FormattedTime
            valueMs={rfid.firstAttempt * 1000}
            doZeroPadding={true}
            showSeconds={true}
          />
        </td>
        <td>
          <FormattedDate valueMs={rfid.lastAttempt * 1000} doZeroPadding={true} />{" "}
          <FormattedTime
            valueMs={rfid.lastAttempt * 1000}
            doZeroPadding={true}
            showSeconds={true}
          />
        </td>
        <td className="center optional">{rfid.counter}</td>
        <td className="edit-col img-link nowrap">
          <span
            tabIndex={0}
            onKeyDown={tabClickHandler}
            onClick={(_evt) => this.props.onAddRfid(rfid.rfid)}
            title={this.props.intl.formatMessage(messages.msgAdd)}
          >
            <IconAdd />
          </span>
        </td>
      </tr>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(RefusedRfidsComponent));
